import { useState } from 'react';
import MultiSelectAutocomplete from '@/components/MultiSelectAutocomplete';
import Checkbox from '@/components/Checkbox';
import Input from '@/components/Input';
import Select from '@/components/Select';
import { Textarea } from '@/components/Textarea';
import DatePickerModal from '@/components/DatePickerModal';
import type { SharedCustomField } from '@/types/serializers';
import { MdKeyboardArrowLeft } from 'react-icons/md';
import { getFieldTypeIcon, getFieldTypeLabel } from '../../customize/utils/fieldTypeHelpers';

interface CandidateImportMappingRowProps {
  customField: SharedCustomField;
  csvHeaders: string[];
  selectedCsvIndices: number[];
  disabledCsvIndices?: number[];
  defaultValue: string;
  onCsvIndicesChange: (indices: number[]) => void;
  onDefaultValueChange: (value: string) => void;
  showValidationError?: boolean;
  disabled?: boolean;
}

export function CandidateImportMappingRow({
  customField,
  csvHeaders,
  selectedCsvIndices,
  disabledCsvIndices = [],
  defaultValue,
  onCsvIndicesChange,
  onDefaultValueChange,
  showValidationError = false,
  disabled = false,
}: CandidateImportMappingRowProps) {
  const [useDefaultValue, setUseDefaultValue] = useState(!!defaultValue);

  const csvHeaderOptions = csvHeaders
    .map((header, index) => ({
      id: index,
      name: header || `列${index + 1}`,
    }))
    .filter(
      (option) =>
        !disabledCsvIndices.includes(option.id as number) ||
        selectedCsvIndices.includes(option.id as number)
    );

  // CSV列選択フィールドのエラー：必須で列が選択されていない、かつ上書きデータモードがOFF
  const csvFieldHasError =
    showValidationError &&
    customField.is_required &&
    selectedCsvIndices.length === 0 &&
    !useDefaultValue;

  // 上書きデータ入力フィールドのエラー：上書きデータモードがONだが値が空
  const defaultValueFieldHasError = showValidationError && useDefaultValue && !defaultValue;

  const handleCsvSelectionChange = (selectedIds: (number | string)[]) => {
    // number型に変換
    const numericIds = selectedIds.map((id) => Number(id)).filter((id) => !isNaN(id));
    onCsvIndicesChange(numericIds);
  };

  const handleUseDefaultValueChange = (checked: boolean) => {
    setUseDefaultValue(checked);
    if (!checked) {
      onDefaultValueChange('');
    }
  };

  /**
   * フィールドタイプに応じた上書きデータ入力フォームをレンダリング
   */
  const renderDefaultValueInput = () => {
    const { field_type, custom_field_options } = customField;
    const errorClassName = defaultValueFieldHasError ? 'border-danger bg-error-light' : '';

    switch (field_type) {
      case 'short_text':
        return (
          <Input
            type="text"
            value={defaultValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onDefaultValueChange(e.target.value)
            }
            placeholder="上書きデータを入力"
            className={`w-full ${errorClassName}`}
          />
        );

      case 'long_text':
        return (
          <Textarea
            value={defaultValue}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              onDefaultValueChange(e.target.value)
            }
            placeholder="上書きデータを入力"
            className={`min-h-[80px] w-full ${errorClassName}`}
          />
        );

      case 'number':
        return (
          <Input
            type="number"
            value={defaultValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onDefaultValueChange(e.target.value)
            }
            placeholder="数値を入力してください"
            className={`w-full ${errorClassName}`}
          />
        );

      case 'single_select':
        return (
          <Select
            items={custom_field_options.map((option) => ({
              value: option.value,
              label: option.value,
            }))}
            value={defaultValue}
            onValueChange={onDefaultValueChange}
            defaultLabel="上書きデータを入力してください"
            className={`w-full ${errorClassName}`}
          />
        );

      case 'multiple_select':
        return (
          <MultiSelectAutocomplete
            options={custom_field_options.map((option) => ({
              id: option.value,
              name: option.value,
            }))}
            value={defaultValue ? defaultValue.split(',') : []}
            onChange={(selectedIds: (number | string)[]) => {
              onDefaultValueChange(selectedIds.join(','));
            }}
            placeholder="選択してください"
            error={defaultValueFieldHasError ? ' ' : undefined}
            className={defaultValueFieldHasError ? '[&_p]:hidden' : ''}
          />
        );

      case 'date':
        return (
          <DatePickerModal
            value={defaultValue}
            onChange={onDefaultValueChange}
            placeholder="日付を選択"
            type="date"
            className={`w-full ${errorClassName}`}
          />
        );

      case 'datetime':
        return (
          <DatePickerModal
            value={defaultValue}
            onChange={onDefaultValueChange}
            placeholder="日時を選択"
            type="datetime"
            className={`w-full ${errorClassName}`}
          />
        );

      default:
        return (
          <Input
            type="text"
            value={defaultValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onDefaultValueChange(e.target.value)
            }
            placeholder="上書きデータを入力してください"
            className={`w-full ${errorClassName}`}
          />
        );
    }
  };

  return (
    <div className="flex items-center gap-md border-b border-solid border-border p-md">
      {/* 情報タイプ */}
      <div className="flex w-[132px] flex-col gap-xs">
        <div className="flex h-6 w-fit items-center gap-xxs rounded-full bg-tag-gray px-sm">
          {getFieldTypeIcon(customField.field_type)}
          <span className="text-xs text-tag-gray">{getFieldTypeLabel(customField.field_type)}</span>
        </div>
      </div>

      {/* フィールド名 */}
      <div className="w-[160px]">
        <p className="text-heading-sm">{customField.name}</p>
      </div>

      {/* アイコン（矢印） */}
      <MdKeyboardArrowLeft size={24} className="text-sub" />

      {/* CSV列選択 + 上書きデータ入力 */}
      <div className="w-[236px]">
        <MultiSelectAutocomplete
          options={csvHeaderOptions}
          value={selectedCsvIndices}
          onChange={handleCsvSelectionChange}
          placeholder="テキスト"
          disabled={useDefaultValue || disabled}
          error={csvFieldHasError ? ' ' : undefined}
          className={csvFieldHasError ? '[&_p]:hidden' : ''}
        />
      </div>
      <div className="flex flex-1 items-center gap-md">
        <Checkbox
          checked={useDefaultValue}
          onChange={handleUseDefaultValueChange}
          disabled={disabled}
        >
          <span className="text-body-sm! font-normal!">上書きする</span>
        </Checkbox>

        {useDefaultValue && <div className="w-[236px]">{renderDefaultValueInput()}</div>}
      </div>
    </div>
  );
}
